import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { AdminSettings, AircraftType, Airstrip, AirstripDistance } from '../../types';
import { resolveRouteMatrixMetrics, routeMatrixKtsToMph } from '../../utils/routeMatrix';

type RouteMatrixTabProps = {
  airstrips: Airstrip[];
  aircrafts: AircraftType[];
  settings: AdminSettings;
  distances: AirstripDistance[];
  distanceImportSummary: {
    processed: number;
    skipped: number;
    errors: Array<{ row: number; message: string }>;
  } | null;
  isImportingDistances: boolean;
  selectedAircraftId: string;
  onSelectAircraft: (id: string) => void;
  onUpdateRouteDefaults: (updates: Partial<AdminSettings['routeDefaults']>) => void;
  onUpdateAircraftRoutePricing: (aircraftId: string, updates: Partial<AircraftType>) => void;
  onImportDistances: (items: Array<{ fromCode: string; toCode: string; distanceMiles: number }>) => void;
  onUpdateDistance: (
    fromCode: string,
    toCode: string,
    payload: {
      distanceMiles?: number;
      overrideSpeedKts?: number;
      overrideRatePerMileUSD?: number;
      overrideRatePerHourUSD?: number;
      overrideBlockMinutes?: number;
    }
  ) => void;
  onDeleteDistance: (fromCode: string, toCode: string) => void;
};

const DEFAULT_SPEED_KTS = 120;
const KTS_TO_MPH = routeMatrixKtsToMph;

export const RouteMatrixTab: React.FC<RouteMatrixTabProps> = ({
  airstrips,
  aircrafts,
  settings,
  distances,
  distanceImportSummary,
  isImportingDistances,
  selectedAircraftId,
  onSelectAircraft,
  onUpdateRouteDefaults,
  onUpdateAircraftRoutePricing,
  onImportDistances,
  onUpdateDistance,
  onDeleteDistance,
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingDistance, setEditingDistance] = useState<string>('');
  const [editingBlockMinutes, setEditingBlockMinutes] = useState<string>('');
  const [editingSpeedKts, setEditingSpeedKts] = useState<string>('');
  const [editingRatePerMile, setEditingRatePerMile] = useState<string>('');
  const [editingRatePerHour, setEditingRatePerHour] = useState<string>('');
  const [editingError, setEditingError] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState<string>('');
  const [showOverridesOnly, setShowOverridesOnly] = useState(false);
  const [page, setPage] = useState(1);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resolveAirstripCode = (raw: unknown) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    const upper = value.toUpperCase();
    const byCode = airstrips.find(item => String(item.code || '').trim().toUpperCase() === upper);
    if (byCode?.code) return String(byCode.code).toUpperCase();
    const byIcao = airstrips.find(item => String(item.icao || '').trim().toUpperCase() === upper);
    if (byIcao?.code) return String(byIcao.code).toUpperCase();
    const byName = airstrips.find(item => String(item.name || '').trim().toUpperCase() === upper);
    if (byName?.code) return String(byName.code).toUpperCase();
    return upper;
  };

  const aircraft = aircrafts.find(item => item.id === selectedAircraftId) || aircrafts[0];
  const routeDefaults = settings.routeDefaults || {
    cruiseSpeedKts: DEFAULT_SPEED_KTS,
    ratePerNmUSD: 0,
    ratePerHourUSD: 0,
    distanceComputationModel: 'GEODESIC',
    distanceBufferPercent: 0,
    distanceBaseMiles: 0,
    roundDistanceUp: false,
    blockTimeMultiplier: 1,
    blockTimeBaseMinutes: 0,
    blockTimeRoundingMinutes: 1,
  };

  const effectiveSpeed = aircraft?.useGlobalRouteSpeed
    ? routeDefaults.cruiseSpeedKts || DEFAULT_SPEED_KTS
    : aircraft?.cruiseSpeedKts || DEFAULT_SPEED_KTS;

  const effectiveRatePerNm = aircraft?.useGlobalRouteRates
    ? routeDefaults.ratePerNmUSD
    : (aircraft?.routeRatePerNmUSD || 0) > 0
      ? aircraft?.routeRatePerNmUSD || 0
      : routeDefaults.ratePerNmUSD;

  const effectiveRatePerHour = aircraft?.useGlobalRouteRates
    ? routeDefaults.ratePerHourUSD
    : (aircraft?.routeRatePerHourUSD || 0) > 0
      ? aircraft?.routeRatePerHourUSD || 0
      : routeDefaults.ratePerHourUSD;

  const globalDefaults = useMemo(() => ({
    speedKts: routeDefaults.cruiseSpeedKts || DEFAULT_SPEED_KTS,
    ratePerNmUSD: routeDefaults.ratePerNmUSD,
    ratePerHourUSD: routeDefaults.ratePerHourUSD,
    distanceComputationModel: routeDefaults.distanceComputationModel || 'GEODESIC',
    distanceBufferPercent: routeDefaults.distanceBufferPercent || 0,
    distanceBaseMiles: routeDefaults.distanceBaseMiles || 0,
    roundDistanceUp: Boolean(routeDefaults.roundDistanceUp),
    blockTimeMultiplier: routeDefaults.blockTimeMultiplier || 1,
    blockTimeBaseMinutes: routeDefaults.blockTimeBaseMinutes || 0,
    blockTimeRoundingMinutes: routeDefaults.blockTimeRoundingMinutes || 1,
  }), [routeDefaults]);

  const aircraftDefaults = useMemo(() => ({
    speedKts: (aircraft?.cruiseSpeedKts || 0) > 0 ? aircraft?.cruiseSpeedKts || 0 : globalDefaults.speedKts,
    ratePerNmUSD: (aircraft?.routeRatePerNmUSD || 0) > 0 ? aircraft?.routeRatePerNmUSD || 0 : globalDefaults.ratePerNmUSD,
    ratePerHourUSD: (aircraft?.routeRatePerHourUSD || 0) > 0 ? aircraft?.routeRatePerHourUSD || 0 : globalDefaults.ratePerHourUSD,
  }), [aircraft, globalDefaults]);

  const distanceOverrides = useMemo(() => {
    const map = new Map<string, AirstripDistance>();
    distances.forEach(item => {
      map.set(`${item.fromCode}::${item.toCode}`, item);
    });
    return map;
  }, [distances]);

  const parseNumber = (value: any) => {
    if (value === null || value === undefined) return 0;
    const cleaned = String(value).replace(/[^0-9.+-]/g, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const handleExportExcel = () => {
    const rows = pairsForExport.map(pair => ({
      FROM: pair.from.code,
      TO: pair.to.code,
      TOTAL_MILES: pair.distanceMiles,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: ['FROM', 'TO', 'TOTAL_MILES'] });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Distances');
    const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([output], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'airstrip-distance-matrix.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleImportExcel = async (file: File) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as any[][];

    const headerRowIndex = rows.findIndex(row => row.some(cell => String(cell).toUpperCase().trim() === 'FROM'));
    if (headerRowIndex === -1) {
      alert('No FROM column found in the uploaded file.');
      return;
    }

    const headers = rows[headerRowIndex].map(cell => String(cell).toUpperCase().trim());
    const colIndex = (name: string) => headers.findIndex(h => h === name);
    const fromIndex = colIndex('FROM');
    const toIndex = colIndex('TO');
    if (fromIndex === -1 || toIndex === -1) {
      alert('FROM/TO columns are required in the uploaded file.');
      return;
    }

    const totalIndex =
      colIndex('TOTAL_MILES') !== -1
        ? colIndex('TOTAL_MILES')
        : colIndex('TOTAL') !== -1
        ? colIndex('TOTAL')
        : colIndex('MILEAGE');

    const segmentIndexes = ['A-B', 'B-C', 'C-D'].map(col => colIndex(col)).filter(index => index !== -1);

    const items: Array<{ fromCode: string; toCode: string; distanceMiles: number }> = [];
    rows.slice(headerRowIndex + 1).forEach(row => {
      const fromCode = resolveAirstripCode(row[fromIndex]);
      const toCode = resolveAirstripCode(row[toIndex]);
      if (!fromCode || !toCode) return;

      let distanceMiles = 0;
      if (totalIndex !== -1) {
        distanceMiles = parseNumber(row[totalIndex]);
      }
      if (!distanceMiles && segmentIndexes.length) {
        distanceMiles = segmentIndexes.reduce((sum, idx) => sum + parseNumber(row[idx]), 0);
      }
      if (distanceMiles <= 0) return;

      items.push({
        fromCode,
        toCode,
        distanceMiles,
      });
    });

    if (!items.length) {
      alert('No valid distance rows found in the uploaded file.');
      return;
    }
    onImportDistances(items);
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedQuery(query);
    }, 250);
    return () => clearTimeout(timeout);
  }, [query]);

  const pairs = useMemo(() => {
    const normalizedQuery = debouncedQuery.trim().toLowerCase();
    const result: Array<{
      from: Airstrip;
      to: Airstrip;
      distanceMiles: number;
      distanceKm: number;
      distanceNm: number;
      blockMinutes: number;
      isOverride: boolean;
    }> = [];

    airstrips.forEach(from => {
      airstrips.forEach(to => {
        if (from.code === to.code) return;
        const label = `${from.code} ${from.name} ${to.code} ${to.name}`.toLowerCase();
        if (normalizedQuery && !label.includes(normalizedQuery)) return;
        const override = distanceOverrides.get(`${from.code}::${to.code}`);
        const metrics = resolveRouteMatrixMetrics({ from, to, routeDefaults, override });
        const distanceMiles = metrics.distanceMiles;
        const distanceKm = distanceMiles * 1.60934;
        const distanceNm = distanceKm / 1.852;
        const blockMinutes = metrics.blockMinutes;
        result.push({
          from,
          to,
          distanceMiles: Number(distanceMiles.toFixed(2)),
          distanceKm: Number(distanceKm.toFixed(2)),
          distanceNm: Number(distanceNm.toFixed(2)),
          blockMinutes,
          isOverride: Boolean(override),
        });
      });
    });

    return result;
  }, [airstrips, debouncedQuery, effectiveSpeed, distanceOverrides, routeDefaults]);

  const filteredPairs = useMemo(() => {
    if (!showOverridesOnly) return pairs;
    return pairs.filter(pair => pair.isOverride);
  }, [pairs, showOverridesOnly]);

  const pairsForExport = useMemo(() => pairs, [pairs]);
  const overrideCount = useMemo(() => pairs.filter(pair => pair.isOverride).length, [pairs]);
  const PAGE_SIZE = 200;
  const totalPages = Math.max(1, Math.ceil(filteredPairs.length / PAGE_SIZE));
  const pagedPairs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredPairs.slice(start, start + PAGE_SIZE);
  }, [filteredPairs, page]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  return (
    <div className="space-y-10">
      <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-800 uppercase tracking-[0.2em]">Route Matrix Intelligence</h3>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Global distance + pricing defaults</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Cruise Speed (KTS)</p>
            <input
              type="number"
              className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
              value={routeDefaults.cruiseSpeedKts}
              onChange={e => onUpdateRouteDefaults({ cruiseSpeedKts: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Rate / NM (USD)</p>
            <input
              type="number"
              className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
              value={routeDefaults.ratePerNmUSD}
              onChange={e => onUpdateRouteDefaults({ ratePerNmUSD: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Rate / Hour (USD)</p>
            <input
              type="number"
              className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
              value={routeDefaults.ratePerHourUSD}
              onChange={e => onUpdateRouteDefaults({ ratePerHourUSD: Number(e.target.value) || 0 })}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Distance Model</p>
            <select
              className="w-full bg-white rounded-xl p-4 text-xs font-black uppercase tracking-widest border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10 text-slate-700"
              value={routeDefaults.distanceComputationModel || 'GEODESIC'}
              onChange={e => onUpdateRouteDefaults({ distanceComputationModel: (e.target.value as 'GEODESIC' | 'PLANAR') })}
            >
              <option value="GEODESIC">Geodesic (Great Circle)</option>
              <option value="PLANAR">Planar Grid (XY)</option>
            </select>
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Distance Buffer (%)</p>
            <input
              type="number"
              className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
              value={routeDefaults.distanceBufferPercent || 0}
              onChange={e => onUpdateRouteDefaults({ distanceBufferPercent: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Base Distance (MI)</p>
            <input
              type="number"
              className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
              value={routeDefaults.distanceBaseMiles || 0}
              onChange={e => onUpdateRouteDefaults({ distanceBaseMiles: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Round Up Miles</p>
              <button
                onClick={() => onUpdateRouteDefaults({ roundDistanceUp: !routeDefaults.roundDistanceUp })}
                className={`px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest ${routeDefaults.roundDistanceUp ? 'bg-indigo-600 text-white' : 'bg-white text-slate-400 border border-slate-200'}`}
              >
                {routeDefaults.roundDistanceUp ? 'Enabled' : 'Disabled'}
              </button>
            </div>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
              Applied only when a row has no manual distance override.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Block Time Multiplier</p>
            <input
              type="number"
              step="0.01"
              className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
              value={routeDefaults.blockTimeMultiplier || 1}
              onChange={e => onUpdateRouteDefaults({ blockTimeMultiplier: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Block Time Base (MIN)</p>
            <input
              type="number"
              className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
              value={routeDefaults.blockTimeBaseMinutes || 0}
              onChange={e => onUpdateRouteDefaults({ blockTimeBaseMinutes: Number(e.target.value) || 0 })}
            />
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Block Time Round (MIN)</p>
            <input
              type="number"
              min={1}
              className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
              value={routeDefaults.blockTimeRoundingMinutes || 1}
              onChange={e => onUpdateRouteDefaults({ blockTimeRoundingMinutes: Number(e.target.value) || 1 })}
            />
          </div>
        </div>
      </section>

      <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-800 uppercase tracking-[0.2em]">Aircraft Route Pricing</h3>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Override global defaults per fleet asset</p>
          </div>
          <select
            className="bg-slate-50 rounded-2xl px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-600"
            value={selectedAircraftId}
            onChange={e => onSelectAircraft(e.target.value)}
          >
            {aircrafts.map(item => (
              <option key={item.id} value={item.id}>
                {item.tailNumber} · {item.name}
              </option>
            ))}
          </select>
        </div>

        {aircraft && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Use Global Speed</p>
                <button
                  onClick={() => onUpdateAircraftRoutePricing(aircraft.id, { useGlobalRouteSpeed: !aircraft.useGlobalRouteSpeed })}
                  className={`px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest ${aircraft.useGlobalRouteSpeed ? 'bg-indigo-600 text-white' : 'bg-white text-slate-400 border border-slate-200'}`}
                >
                  {aircraft.useGlobalRouteSpeed ? 'Enabled' : 'Disabled'}
                </button>
              </div>
              <input
                type="number"
                className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
                value={aircraft.cruiseSpeedKts || DEFAULT_SPEED_KTS}
                onChange={e => onUpdateAircraftRoutePricing(aircraft.id, { cruiseSpeedKts: Number(e.target.value) || 0 })}
                disabled={aircraft.useGlobalRouteSpeed}
              />
            </div>

            <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Use Global Rates</p>
                <button
                  onClick={() => onUpdateAircraftRoutePricing(aircraft.id, { useGlobalRouteRates: !aircraft.useGlobalRouteRates })}
                  className={`px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest ${aircraft.useGlobalRouteRates ? 'bg-indigo-600 text-white' : 'bg-white text-slate-400 border border-slate-200'}`}
                >
                  {aircraft.useGlobalRouteRates ? 'Enabled' : 'Disabled'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="number"
                  className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
                  value={aircraft.routeRatePerNmUSD || 0}
                  onChange={e => onUpdateAircraftRoutePricing(aircraft.id, { routeRatePerNmUSD: Number(e.target.value) || 0 })}
                  disabled={aircraft.useGlobalRouteRates}
                  placeholder="$/NM"
                />
                <input
                  type="number"
                  className="w-full bg-white rounded-xl p-4 text-sm font-black font-mono border border-slate-100 outline-none focus:ring-2 focus:ring-indigo-500/10"
                  value={aircraft.routeRatePerHourUSD || 0}
                  onChange={e => onUpdateAircraftRoutePricing(aircraft.id, { routeRatePerHourUSD: Number(e.target.value) || 0 })}
                  disabled={aircraft.useGlobalRouteRates}
                  placeholder="$/HR"
                />
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-10 border-b border-slate-50 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 bg-slate-50/30">
          <div>
            <h3 className="text-lg font-black text-slate-800 uppercase tracking-[0.2em]">Airstrip Distance Matrix</h3>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">All route pairs, auto-calculated</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <input
              className="bg-white border border-slate-200 rounded-2xl px-5 py-3 text-xs font-bold text-slate-600 w-full lg:w-80"
              placeholder="Search airstrip pair"
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
            <button
              onClick={() => setShowOverridesOnly(prev => !prev)}
              className={`px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border ${showOverridesOnly ? 'border-indigo-200 text-indigo-600 bg-indigo-50' : 'border-slate-200 text-slate-500 bg-white'}`}
            >
              {showOverridesOnly ? 'Overrides Only' : 'All Routes'}
            </button>
            <button
              onClick={handleSelectFile}
              className="px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-indigo-200 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-all"
              disabled={isImportingDistances}
            >
              {isImportingDistances ? 'Importing...' : 'Import Excel'}
            </button>
            <button
              onClick={handleExportExcel}
              className="px-4 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 transition-all"
              disabled={pairsForExport.length === 0}
            >
              Export Excel
            </button>
          </div>
        </div>
        <div className="px-10 py-4 bg-white border-b border-slate-50 grid grid-cols-1 lg:grid-cols-[180px_1fr_220px] gap-4 text-[9px] font-black uppercase tracking-widest text-slate-400">
          <span>Route Pair</span>
          <div className="grid grid-cols-3 gap-4">
            <span>Distance (MI)</span>
            <span>Block Time (MIN)</span>
            <span>Metric (KM / NM)</span>
          </div>
          <span className="text-right">Pricing (Mileage / Hours)</span>
        </div>
        <div className="px-10 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 bg-white border-b border-slate-50 flex flex-wrap gap-6">
          <span>Total Pairs: {pairs.length}</span>
          <span>Overrides: {overrideCount}</span>
          <span>Showing: {filteredPairs.length}</span>
          <span>Page: {page} / {totalPages}</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0];
            if (!file) return;
            handleImportExcel(file);
            e.currentTarget.value = '';
          }}
        />
        {distanceImportSummary && (
          <div className="px-10 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 bg-white border-b border-slate-50 flex flex-wrap gap-6">
            <span>Processed: {distanceImportSummary.processed}</span>
            <span>Skipped: {distanceImportSummary.skipped}</span>
            {distanceImportSummary.errors.length > 0 && (
              <span className="text-rose-500">Errors: {distanceImportSummary.errors.length}</span>
            )}
          </div>
        )}
        <div className="p-10 space-y-4 max-h-[520px] overflow-y-auto">
          {filteredPairs.length === 0 && (
            <div className="text-center text-sm font-bold text-slate-400 py-10">No routes match the current filters.</div>
          )}
          {pagedPairs.map((pair, index) => {
            const rowKey = `${pair.from.code}::${pair.to.code}`;
            const override = distanceOverrides.get(rowKey);
            const overrideRatePerMileUSD = override?.overrideRatePerMileUSD || 0;
            const overrideRatePerHourUSD = override?.overrideRatePerHourUSD || 0;
            const ratePerMile = overrideRatePerMileUSD > 0 ? overrideRatePerMileUSD : effectiveRatePerNm;
            const ratePerHour = overrideRatePerHourUSD > 0 ? overrideRatePerHourUSD : effectiveRatePerHour;
            const costPerMile = pair.distanceMiles * ratePerMile;
            const costPerHour = (pair.blockMinutes / 60) * ratePerHour;
            return (
              <div key={`${pair.from.code}-${pair.to.code}-${index}`} className="grid grid-cols-1 lg:grid-cols-[180px_1fr_220px] gap-4 items-center bg-slate-50/50 rounded-2xl p-5">
                <div className="text-sm font-black text-slate-800 uppercase">{pair.from.code} ➔ {pair.to.code}</div>
                <div className="grid grid-cols-3 gap-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  <span>{pair.distanceMiles} MI {pair.isOverride && <em className="text-indigo-500 not-italic">OVR</em>}</span>
                  <span>{pair.blockMinutes} MIN</span>
                  <span>{pair.distanceKm} KM / {pair.distanceNm} NM</span>
                </div>
                <div className="flex items-center justify-end gap-4">
                  <div className="text-right text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <div>Mileage: ${costPerMile.toFixed(0)}</div>
                    <div>Hours: ${costPerHour.toFixed(0)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditingKey(rowKey);
                        setEditingLabel(`${pair.from.code} → ${pair.to.code}`);
                        setEditingDistance(String(pair.distanceMiles));
                        setEditingBlockMinutes(String(override?.overrideBlockMinutes || ''));
                        setEditingSpeedKts(String(override?.overrideSpeedKts || ''));
                        setEditingRatePerMile(String(override?.overrideRatePerMileUSD || ''));
                        setEditingRatePerHour(String(override?.overrideRatePerHourUSD || ''));
                        setEditingError(null);
                      }}
                      className="px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest bg-white text-slate-500 border border-slate-200"
                    >
                      Edit
                    </button>
                    {override && (
                      <button
                        onClick={() => {
                          if (!confirm(`Remove overrides for ${pair.from.code} → ${pair.to.code}? This resets to auto-calculated values.`)) {
                            return;
                          }
                          onDeleteDistance(pair.from.code, pair.to.code);
                        }}
                        className="px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest bg-rose-50 text-rose-600 border border-rose-200"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {filteredPairs.length > PAGE_SIZE && (
          <div className="px-10 pb-10 flex items-center justify-between">
            <button
              className="px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-200 text-slate-500 disabled:opacity-40"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Page {page} of {totalPages}
            </div>
            <button
              className="px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-200 text-slate-500 disabled:opacity-40"
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </section>
      {editingKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-xl rounded-3xl bg-white p-8 shadow-2xl border border-slate-100">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h4 className="text-lg font-black text-slate-800 uppercase tracking-widest">Edit Route Overrides</h4>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{editingLabel}</p>
              </div>
              <button
                onClick={() => {
                  setEditingKey(null);
                  setEditingError(null);
                }}
                className="w-10 h-10 rounded-2xl bg-slate-50 text-slate-400 hover:bg-rose-500 hover:text-white transition-all"
              >
                ✕
              </button>
            </div>
            {editingError && (
              <div className="mb-4 text-[10px] font-black uppercase tracking-widest text-rose-500">{editingError}</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Distance (Miles)</label>
                <input
                  type="number"
                  className="w-full bg-slate-50 rounded-xl p-3 text-sm font-black font-mono border border-slate-200 outline-none"
                  value={editingDistance}
                  onChange={e => {
                    setEditingDistance(e.target.value);
                    setEditingError(null);
                  }}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Block Minutes (Override)</label>
                <input
                  type="number"
                  className="w-full bg-slate-50 rounded-xl p-3 text-sm font-black font-mono border border-slate-200 outline-none"
                  value={editingBlockMinutes}
                  onChange={e => setEditingBlockMinutes(e.target.value)}
                />
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Global: Auto-calculated</p>
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Speed KTS (Override)</label>
                <input
                  type="number"
                  className="w-full bg-slate-50 rounded-xl p-3 text-sm font-black font-mono border border-slate-200 outline-none"
                  value={editingSpeedKts}
                  onChange={e => setEditingSpeedKts(e.target.value)}
                />
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Global: {globalDefaults.speedKts} kts · Aircraft: {aircraftDefaults.speedKts} kts</p>
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Rate / NM USD (Override)</label>
                <input
                  type="number"
                  className="w-full bg-slate-50 rounded-xl p-3 text-sm font-black font-mono border border-slate-200 outline-none"
                  value={editingRatePerMile}
                  onChange={e => setEditingRatePerMile(e.target.value)}
                />
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Global: ${globalDefaults.ratePerNmUSD} / nm · Aircraft: ${aircraftDefaults.ratePerNmUSD} / nm</p>
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Rate / Hour USD (Override)</label>
                <input
                  type="number"
                  className="w-full bg-slate-50 rounded-xl p-3 text-sm font-black font-mono border border-slate-200 outline-none"
                  value={editingRatePerHour}
                  onChange={e => setEditingRatePerHour(e.target.value)}
                />
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Global: ${globalDefaults.ratePerHourUSD} / hour · Aircraft: ${aircraftDefaults.ratePerHourUSD} / hour</p>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                Tip: set 0 to use global defaults.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setEditingKey(null);
                    setEditingError(null);
                  }}
                  className="px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest bg-white text-slate-500 border border-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const distanceValue = parseNumber(editingDistance);
                    if (distanceValue <= 0) {
                      setEditingError('Distance must be greater than 0');
                      return;
                    }
                    const [fromCode, toCode] = editingKey.split('::');
                    onUpdateDistance(fromCode, toCode, {
                      distanceMiles: distanceValue,
                      overrideBlockMinutes: parseNumber(editingBlockMinutes),
                      overrideSpeedKts: parseNumber(editingSpeedKts),
                      overrideRatePerMileUSD: parseNumber(editingRatePerMile),
                      overrideRatePerHourUSD: parseNumber(editingRatePerHour),
                    });
                    setEditingKey(null);
                    setEditingError(null);
                  }}
                  className="px-5 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest bg-indigo-600 text-white"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
